// JOBE-0098.2 / JOBE-0044.10: friendly-token → wire-predicate vocabulary.
//
// This module is the canonical source of truth for the omni-parser
// (JOBE-0098.2) and the filter composer picker (JOBE-0044.10). It
// supersedes the Phase 2 `src/web/ui/src/library/chipVocabulary.js`,
// which remains in place as a Phase 2 subset until a Phase 4
// consolidation. Both views over the same data.
//
// JOBE-0044.10 ships only the vocabulary data (FRIENDLY_VOCABULARY,
// FRIENDLY_ALIASES, RETIRED_TOKENS, RELATIVE_DATE_TOKENS). JOBE-0098.2
// will later add `parse()` and `splitTokenOp()` into this directory
// (operators.js, parse.js, index.js). The picker reads
// FRIENDLY_VOCABULARY and groups by `.group`.
//
// Retired tokens (ADR-0023 §D2) are listed explicitly so the parser
// and the picker can short-circuit them with `reason: 'retired'`;
// they MUST NOT appear in any picker group, tooltip, or
// search-match.

// FRIENDLY_VOCABULARY: ordered array of vocabulary entries. The picker
// renders groups in the order they first appear; within a group the
// option order is the array order.
//
// Each entry shape:
//   friendlyToken : string  — the canonical user-facing token
//   wirePredicate : string  — the BFF / query_entities predicate name
//   group         : 'People' | 'Place' | 'Lineage' | 'Linkage' | 'Schema'
//   label         : string  — picker row left-side title (Inter Medium)
//   gloss         : string  — italic Inter sub-text on the picker row
//   valuePopover  : string  — sub-popover component key (see picker)
//   op            : ':' | '>=' | '<=' | '>' | '<' | '!='  (default ':')
//   notes         : string  — short hint string, optional
export const FRIENDLY_VOCABULARY = [
  // ── People ────────────────────────────────────────────────────
  {
    friendlyToken: 'by',
    wirePredicate: 'committedBy',
    group: 'People',
    label: 'Created by',
    gloss: 'whose hand wrote it',
    valuePopover: 'people',
  },
  {
    friendlyToken: 'owner',
    wirePredicate: 'assignedTo',
    group: 'People',
    label: 'Owned by',
    gloss: 'current assignee',
    valuePopover: 'people',
  },
  {
    friendlyToken: 'watcher',
    wirePredicate: 'watches',
    group: 'People',
    label: 'Watched by',
    gloss: 'inverse follow edge',
    valuePopover: 'people',
  },
  {
    friendlyToken: 'dept',
    wirePredicate: 'roleInDepartment',
    group: 'People',
    label: 'Department of owner',
    gloss: 'org-graph join',
    valuePopover: 'scalar',
    notes: 'free-text in v1; a dept picker may replace this',
  },

  // ── Place ─────────────────────────────────────────────────────
  {
    friendlyToken: 'project',
    wirePredicate: 'inProject',
    group: 'Place',
    label: 'In project',
    gloss: 'project membership',
    valuePopover: 'scalar',
    notes: 'a project picker may replace this',
  },
  {
    friendlyToken: 'in',
    wirePredicate: 'inCollection',
    group: 'Place',
    label: 'In collection',
    gloss: 'curated collection containment',
    valuePopover: 'collection',
  },
  {
    friendlyToken: 'parent',
    wirePredicate: 'partOf',
    group: 'Place',
    label: 'Part of',
    gloss: 'hierarchy or sub-task edge',
    valuePopover: 'scalar',
  },

  // ── Lineage ───────────────────────────────────────────────────
  {
    friendlyToken: 'from',
    wirePredicate: 'derivedFrom',
    group: 'Lineage',
    label: 'Derived from',
    gloss: 'source-artifact provenance',
    valuePopover: 'scalar',
  },
  {
    friendlyToken: 'by-run',
    wirePredicate: 'producedByRun',
    group: 'Lineage',
    label: 'Produced by',
    gloss: 'run URN that wrote this',
    valuePopover: 'procedure',
  },
  {
    friendlyToken: 'procedure',
    wirePredicate: 'procedureUrn',
    group: 'Lineage',
    label: 'Procedure',
    gloss: 'any / specific URN / one-off',
    valuePopover: 'procedure',
  },
  {
    friendlyToken: 'cites',
    wirePredicate: 'references',
    group: 'Lineage',
    label: 'References',
    gloss: 'citation-style edge',
    valuePopover: 'scalar',
  },

  // ── Linkage ───────────────────────────────────────────────────
  {
    friendlyToken: 'about',
    wirePredicate: 'relatesTo',
    group: 'Linkage',
    label: 'About',
    gloss: 'weak semantic link',
    valuePopover: 'scalar',
  },
  {
    friendlyToken: 'blocked-by',
    wirePredicate: 'dependsOn',
    group: 'Linkage',
    label: 'Depends on',
    gloss: 'informational dependency',
    valuePopover: 'scalar',
  },

  // ── Schema ────────────────────────────────────────────────────
  {
    friendlyToken: 'type',
    wirePredicate: 'type',
    group: 'Schema',
    label: 'Has type',
    gloss: 'entity kind filter',
    valuePopover: 'type',
  },
  {
    friendlyToken: 'since',
    wirePredicate: 'committedAt>=',
    group: 'Schema',
    label: 'Updated on or after',
    gloss: 'date predicate',
    valuePopover: 'date',
    op: '>=',
  },
  {
    friendlyToken: 'until',
    wirePredicate: 'committedAt<',
    group: 'Schema',
    label: 'Updated before',
    gloss: 'date predicate',
    valuePopover: 'date',
    op: '<',
  },
  {
    friendlyToken: 'created',
    wirePredicate: 'createdAt',
    group: 'Schema',
    label: 'Created on or after',
    gloss: 'date predicate',
    valuePopover: 'date',
    op: '>=',
  },
];

// Group order for the picker (per JOBE-0044 §R4). "Schema fields" is a
// synthetic group rendered separately from FRIENDLY_VOCABULARY — see
// JOBE-0044.10 SchemaFieldPicker.
export const GROUP_ORDER = ['People', 'Place', 'Lineage', 'Linkage', 'Schema'];

// Subtitle gloss per group (italic Inter under the JetBrains Mono
// group header).
export const GROUP_SUBTITLES = Object.freeze({
  People: 'who made or owns it',
  Place: 'where it sits',
  Lineage: 'provenance walk',
  Linkage: 'weak / informational',
  Schema: 'scalar, per selected kind',
});

// FRIENDLY_ALIASES: alternate spellings collapse to the canonical
// friendly token. Parser callers resolve via `resolveAlias`.
//
// Compound-key aliases (`'updated>='`, `'updated<'`, `'created>='`,
// `'created<'`) are intentionally absent: the parser strips the
// operator before alias lookup via `splitTokenOp`, so it never
// queries the alias map for the compound form. Including them only
// matters for `findVocabularyByFriendlyToken` helper callers — and
// in v1 there are none — so they are omitted in favour of a single
// coherent map. `'created<'` would additionally be semantically
// wrong (it would re-route `createdAt<...` to `committedAt<...`).
// JOBE-0044.10.1 PR #697 removed the four compound-key entries
// together rather than mixing one removal with three retained-but-
// dead entries.
export const FRIENDLY_ALIASES = Object.freeze({
  creator: 'by',
});

// RETIRED_TOKENS: ADR-0023 §D2. The parser emits an
// UnrecognizedFragment with `reason: 'retired'` for these; the picker
// MUST NOT render them in any group, tooltip, or search match.
export const RETIRED_TOKENS = Object.freeze(new Set([
  'memberOfProject',
  'projectRef',
]));

// RELATIVE_DATE_TOKENS: parser-recognised relative date strings (the
// resolver translates to ISO at fetch time per JOBE-0098.2). The
// DateValuePopover renders these as one-click shortcuts.
export const RELATIVE_DATE_TOKENS = Object.freeze([
  'today',
  'yesterday',
  'this-week',
  'last-week',
  'this-month',
  'last-7d',
  'last-30d',
  'last-90d',
  'last-365d',
]);

// findVocabularyByFriendlyToken: small lookup helper. O(n) — the
// vocabulary is short enough that an index isn't worth the indirection.
export function findVocabularyByFriendlyToken(token) {
  if (!token) return null;
  const canonical = FRIENDLY_ALIASES[token] || token;
  return FRIENDLY_VOCABULARY.find((v) => v.friendlyToken === canonical) || null;
}

// isRetiredToken: true if the raw token is in the retired set OR if
// its canonical (after alias resolution) is retired. Used by the
// parser's `reason: 'retired'` branch and asserted by the picker tests.
export function isRetiredToken(token) {
  if (!token) return false;
  if (RETIRED_TOKENS.has(token)) return true;
  return false;
}
